import { useRef, useEffect, useCallback, useState } from "react";
import { Box, Button, ToggleButton, ToggleButtonGroup } from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { AspectRatio, CropState } from "../types";
import { ASPECT_RATIOS } from "../constants";
import { useTranslation } from "react-i18next";

interface CropOverlayProps {
  canvasWidth: number;
  canvasHeight: number;
  cropState: CropState;
  onCropStateChange: (state: CropState) => void;
  onApply: () => void;
  onCancel: () => void;
  canvasRect: DOMRect | null;
}

const HANDLE_SIZE = 8;

type HandlePosition = "nw" | "n" | "ne" | "e" | "se" | "s" | "sw" | "w";

const HANDLES: HandlePosition[] = ["nw", "n", "ne", "e", "se", "s", "sw", "w"];

function getHandleCursor(handle: HandlePosition): string {
  const cursors: Record<HandlePosition, string> = {
    nw: "nwse-resize",
    n: "ns-resize",
    ne: "nesw-resize",
    e: "ew-resize",
    se: "nwse-resize",
    s: "ns-resize",
    sw: "nesw-resize",
    w: "ew-resize",
  };
  return cursors[handle];
}

function getHandlePosition(
  handle: HandlePosition,
  crop: CropState
): { left: number; top: number } {
  const { x, y, width, height } = crop;
  const positions: Record<HandlePosition, { left: number; top: number }> = {
    nw: { left: x, top: y },
    n: { left: x + width / 2, top: y },
    ne: { left: x + width, top: y },
    e: { left: x + width, top: y + height / 2 },
    se: { left: x + width, top: y + height },
    s: { left: x + width / 2, top: y + height },
    sw: { left: x, top: y + height },
    w: { left: x, top: y + height / 2 },
  };
  return positions[handle];
}

export default function CropOverlay({
  canvasWidth,
  canvasHeight,
  cropState,
  onCropStateChange,
  onApply,
  onCancel,
  canvasRect,
}: CropOverlayProps) {
  const { t } = useTranslation(["bugherd"]);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [initialCrop, setInitialCrop] = useState({ x: 0, y: 0, w: 0, h: 0 });
  const overlayRef = useRef<HTMLDivElement>(null);

  if (!canvasRect) return null;

  const scaleX = canvasRect.width / canvasWidth;
  const scaleY = canvasRect.height / canvasHeight;

  const displayCrop = {
    x: cropState.x * scaleX,
    y: cropState.y * scaleY,
    width: cropState.width * scaleX,
    height: cropState.height * scaleY,
  };

  const toCanvasCoords = (clientX: number, clientY: number) => ({
    x: (clientX - canvasRect.left) / scaleX,
    y: (clientY - canvasRect.top) / scaleY,
  });

  const constrainToAspectRatio = (
    w: number,
    h: number,
    ratio: AspectRatio,
    anchor: "se" | "sw" | "ne" | "nw" | "e" | "w" | "n" | "s"
  ): { width: number; height: number } => {
    const r = ASPECT_RATIOS[ratio];
    if (!r) return { width: w, height: h };
    if (["e", "w", "ne", "se", "nw", "sw"].includes(anchor)) {
      return { width: w, height: w / r };
    }
    return { width: h * r, height: h };
  };

  const handleMouseDown = (
    e: React.MouseEvent,
    action: "drag" | HandlePosition
  ) => {
    e.preventDefault();
    e.stopPropagation();
    const { x: cx, y: cy } = toCanvasCoords(e.clientX, e.clientY);
    setDragStart({ x: cx, y: cy });
    setInitialCrop({
      x: cropState.x,
      y: cropState.y,
      w: cropState.width,
      h: cropState.height,
    });
    onCropStateChange({
      ...cropState,
      isDragging: action === "drag",
      isResizing: action !== "drag",
      resizeHandle: action !== "drag" ? action : null,
      dragStartX: cx,
      dragStartY: cy,
    });
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!cropState.isDragging && !cropState.isResizing) return;
      const { x: cx, y: cy } = toCanvasCoords(e.clientX, e.clientY);
      const dx = cx - dragStart.x;
      const dy = cy - dragStart.y;

      if (cropState.isDragging) {
        let newX = initialCrop.x + dx;
        let newY = initialCrop.y + dy;
        newX = Math.max(0, Math.min(newX, canvasWidth - cropState.width));
        newY = Math.max(0, Math.min(newY, canvasHeight - cropState.height));
        onCropStateChange({ ...cropState, x: newX, y: newY });
        return;
      }

      if (cropState.isResizing && cropState.resizeHandle) {
        const handle = cropState.resizeHandle as HandlePosition;
        let newX = initialCrop.x;
        let newY = initialCrop.y;
        let newW = initialCrop.w;
        let newH = initialCrop.h;

        if (handle.includes("e")) newW = initialCrop.w + dx;
        if (handle.includes("w")) {
          newX = initialCrop.x + dx;
          newW = initialCrop.w - dx;
        }
        if (handle.includes("s")) newH = initialCrop.h + dy;
        if (handle.includes("n")) {
          newY = initialCrop.y + dy;
          newH = initialCrop.h - dy;
        }

        // Enforce minimum size
        if (newW < 20) { newW = 20; if (handle.includes("w")) newX = initialCrop.x + initialCrop.w - 20; }
        if (newH < 20) { newH = 20; if (handle.includes("n")) newY = initialCrop.y + initialCrop.h - 20; }

        // Apply aspect ratio constraint
        if (cropState.aspectRatio !== "free") {
          const constrained = constrainToAspectRatio(newW, newH, cropState.aspectRatio, handle);
          newW = constrained.width;
          newH = constrained.height;
        }

        // Clamp to canvas bounds
        newX = Math.max(0, newX);
        newY = Math.max(0, newY);
        if (newX + newW > canvasWidth) newW = canvasWidth - newX;
        if (newY + newH > canvasHeight) newH = canvasHeight - newY;

        onCropStateChange({
          ...cropState,
          x: newX,
          y: newY,
          width: newW,
          height: newH,
        });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [cropState.isDragging, cropState.isResizing, cropState.resizeHandle, dragStart, initialCrop, canvasWidth, canvasHeight]
  );

  const handleMouseUp = useCallback(() => {
    onCropStateChange({
      ...cropState,
      isDragging: false,
      isResizing: false,
      resizeHandle: null,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cropState]);

  useEffect(() => {
    if (cropState.isDragging || cropState.isResizing) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
      return () => {
        document.removeEventListener("mousemove", handleMouseMove);
        document.removeEventListener("mouseup", handleMouseUp);
      };
    }
  }, [cropState.isDragging, cropState.isResizing, handleMouseMove, handleMouseUp]);

  const handleAspectRatioChange = (_: React.MouseEvent, value: string | null) => {
    if (!value) return;
    const ratio = value as AspectRatio;
    let { width, height } = cropState;

    if (ratio !== "free") {
      const r = ASPECT_RATIOS[ratio]!;
      const currentRatio = width / height;
      if (currentRatio > r) {
        width = height * r;
      } else {
        height = width / r;
      }
    }

    onCropStateChange({ ...cropState, aspectRatio: ratio, width, height });
  };

  return (
    <Box
      ref={overlayRef}
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        width: canvasRect.width,
        height: canvasRect.height,
        zIndex: 5,
        pointerEvents: "auto",
      }}
    >
      {/* Dark overlay (4 regions around crop) */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: displayCrop.y,
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: displayCrop.y,
          left: 0,
          width: displayCrop.x,
          height: displayCrop.height,
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: displayCrop.y,
          left: displayCrop.x + displayCrop.width,
          right: 0,
          height: displayCrop.height,
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      />
      <Box
        sx={{
          position: "absolute",
          top: displayCrop.y + displayCrop.height,
          left: 0,
          width: "100%",
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
        }}
      />

      {/* Crop region border */}
      <Box
        sx={{
          position: "absolute",
          top: displayCrop.y,
          left: displayCrop.x,
          width: displayCrop.width,
          height: displayCrop.height,
          border: "2px solid #fff",
          boxSizing: "border-box",
          cursor: "move",
        }}
        onMouseDown={(e) => handleMouseDown(e, "drag")}
      />

      {/* Resize handles */}
      {HANDLES.map((handle) => {
        const pos = getHandlePosition(handle, {
          ...cropState,
          x: displayCrop.x,
          y: displayCrop.y,
          width: displayCrop.width,
          height: displayCrop.height,
        } as CropState);
        return (
          <Box
            key={handle}
            sx={{
              position: "absolute",
              left: pos.left - HANDLE_SIZE / 2,
              top: pos.top - HANDLE_SIZE / 2,
              width: HANDLE_SIZE,
              height: HANDLE_SIZE,
              backgroundColor: "#fff",
              border: "1px solid #333",
              cursor: getHandleCursor(handle),
              zIndex: 6,
            }}
            onMouseDown={(e) => handleMouseDown(e, handle)}
          />
        );
      })}

      {/* Aspect ratio presets + apply/cancel buttons */}
      <Box
        sx={{
          position: "absolute",
          top: displayCrop.y + displayCrop.height + 8,
          left: displayCrop.x,
          display: "flex",
          gap: 1,
          alignItems: "center",
          zIndex: 7,
        }}
      >
        <ToggleButtonGroup
          value={cropState.aspectRatio}
          exclusive
          onChange={handleAspectRatioChange}
          size="small"
          sx={{
            backgroundColor: "#fff",
            borderRadius: "8px",
            "& .MuiToggleButton-root": {
              textTransform: "none",
              fontSize: 11,
              px: 1,
              py: 0.25,
            },
          }}
        >
          {(Object.keys(ASPECT_RATIOS) as AspectRatio[]).map((key) => (
            <ToggleButton key={key} value={key}>
              {key === "free" ? t("free", "Free") : key}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={onApply}
          startIcon={<CheckIcon />}
          sx={{ textTransform: "none", fontSize: 12, minWidth: 0, px: 1.5 }}
        >
          {t("applyCrop")}
        </Button>
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={onCancel}
          startIcon={<CloseIcon />}
          sx={{
            textTransform: "none",
            fontSize: 12,
            minWidth: 0,
            px: 1.5,
            backgroundColor: "#fff",
          }}
        >
          {t("cancelCrop")}
        </Button>
      </Box>
    </Box>
  );
}
